import { useCallback, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { MultiSelect } from '@/components/ui/multi-select'
import { useDebounce } from '@/hooks/useDebounce'
import { useMfpOptions, usePlanMfps, useSyncPlanMfps } from '@/hooks/useMfps'

interface MfpAssignmentCardProps {
  planId: string
}

export function MfpAssignmentCard({ planId }: MfpAssignmentCardProps) {
  const [search, setSearch] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const debouncedSearch = useDebounce(search, 300)

  const {
    data: mfpPages,
    isLoading: optionsLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useMfpOptions(debouncedSearch, dropdownOpen)

  const { data: planMfps, isLoading: planMfpsLoading } = usePlanMfps(planId)
  const syncMutation = useSyncPlanMfps(planId)

  const selectedIds = useMemo(
    () => (planMfps ?? []).map(m => m.mfp_id),
    [planMfps]
  )

  // Flatten infinite query pages into options
  const searchOptions = useMemo(
    () =>
      (mfpPages?.pages ?? []).flatMap(page =>
        page.data.map(mfp => ({ value: mfp.id, label: mfp.name }))
      ),
    [mfpPages]
  )

  // Merge selected MFP labels (from version data) with search results
  // so already-selected items always show proper labels
  const options = useMemo(() => {
    const searchMap = new Map(searchOptions.map(o => [o.value, o]))
    const merged = [...searchOptions]

    for (const mfp of planMfps ?? []) {
      if (!searchMap.has(mfp.mfp_id)) {
        merged.push({ value: mfp.mfp_id, label: mfp.mfp_name })
      }
    }

    return merged
  }, [searchOptions, planMfps])

  const handleChange = (newIds: string[]) => {
    syncMutation.mutate(newIds)
  }

  const handleClear = () => {
    syncMutation.mutate([])
  }

  const handleReachEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <label className="text-[16px] font-medium text-[#262626]">MFP</label>
        {syncMutation.isPending && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
        )}
      </div>
      <MultiSelect
        options={options}
        value={selectedIds}
        onValueChange={handleChange}
        onSearchChange={setSearch}
        onOpenChange={setDropdownOpen}
        onReachEnd={handleReachEnd}
        isFetchingMore={isFetchingNextPage}
        maxVisibleBadges={Infinity}
        placeholder="Select MFPs..."
        isLoading={optionsLoading || planMfpsLoading}
        badgeClassName="max-w-full"
      />
      {selectedIds.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleClear}
            disabled={syncMutation.isPending}
            className="text-xs italic text-gray-500 hover:text-gray-700 cursor-pointer disabled:opacity-50"
          >
            clear mfp
          </button>
        </div>
      )}
    </div>
  )
}
